import { GenericForm, GenericFormBGContainer } from "@/components/GenericForm";
import { Input } from "@/components/_Shared/Form/Inputs/Input";
import { Divider } from "@/components/misc";
import { FieldValues } from "react-hook-form";
import { Submit } from "@/components/_Shared/Form/Buttons/Submit";  
import { FormFetchDataProps } from "@/components/_Shared/Form/Interfaces/FormFetchData";
import { CreateSelect } from "@/components/_Shared/Form/Inputs/CreateSelect";
import { SearchSelect } from "@/components/_Shared/Form/Inputs/SearchSelect";
import { TextArea } from "@/components/_Shared/Form/Inputs/TextArea";
import { Checkbox } from "@/components/_Shared/Form/Inputs/Checkbox";
import { FormGroup, FormChild } from "@/components/_Shared/Form/Containers/FormGroup";
import { Button } from "@/components/_Shared/Button";
import { useNavigate } from "react-router-dom"; 
import { FormHookDefaults } from "@/components/_Shared/Form/Interfaces/BasePropFormHookDefaults";
import { BidCategoryOptions, BidStatusOptions, BidTemplateOptions } from "@/interfaces/bid_types";

type BidBaseProps<TFormValues extends FieldValues> = {
    manager_list: FormFetchDataProps[]
    city_list: string[]
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
    method: "POST" | "PUT"
    bid_id: string
} & FormHookDefaults<TFormValues>

export function BidFormBase<TFormValues extends FieldValues>({register, control, manager_list, city_list, onSubmit, method, errors, isProcessing, bid_id}: BidBaseProps<TFormValues>) {
    const navigate = useNavigate()

    return (
        <GenericForm form_id="bid_creation" onSubmit={onSubmit}>
            <GenericFormBGContainer>
                <h2 className="text-2xl font-bold">{method === "POST" ? "Create" : "Update"} Bid {bid_id ? `(${bid_id})` : ""}</h2>

                <Divider />

                <Input label="Bid Name" name="bid_name" type="text" register={register} />
                {errors.bid_name && <p className="text-red-500">{errors.bid_name.message}</p>}

                <FormGroup grid_cols={2}>
                    <FormChild>
                        <Input label="Date Created" name="start_date" type="date" register={register} />
                        {errors.start_date && <p className="text-red-500">{errors.start_date.message}</p>}
                    </FormChild>

                    <FormChild>
                        <Input label="Date Due" name="end_date" type="date" register={register} />
                        {errors.end_date && <p className="text-red-500">{errors.end_date.message}</p>}

                        <FormChild layout="row" className="justify-end">
                            <Checkbox label="Deadline" name="isDeadline" register={register} />
                            <Checkbox label="No Date Due" name="no_date_due" register={register} />
                        </FormChild>
                    </FormChild>
                </FormGroup>

                <FormGroup grid_cols={2}>
                    <FormChild>
                        <SearchSelect label="Bid Manager" name="manager" options={manager_list} control={control}  />
                        {errors.manager && <p className="text-red-500">{errors.manager.message}</p>}
                    </FormChild>

                    <FormChild>
                        <CreateSelect label="City" name="city" options={city_list.map((city) => ({value: city, label: city}))} control={control} />
                        {errors.city && <p className="text-red-500">{errors.city.message}</p>}
                    </FormChild>
                </FormGroup>

                <TextArea label="Bid Description" name="description" placeholder="Enter a description for the bid" register={register} />
                {errors.description && <p className="text-red-500">{errors.description.message}</p>}

                <FormGroup grid_cols={2}>
                    <FormChild>
                        <SearchSelect label="Template" name="template" options={BidTemplateOptions} control={control} />
                        {errors.template && <p className="text-red-500">{errors.template.message}</p>}
                    </FormChild>

                    <FormChild>
                        <SearchSelect label="Category" name="category" options={BidCategoryOptions} control={control} />
                        {errors.category && <p className="text-red-500">{errors.category.message}</p>}
                    </FormChild>
                </FormGroup>

                <FormGroup>
                  <FormChild>
                        <SearchSelect label="Status" name="status" options={BidStatusOptions} control={control} />
                        {errors.status && <p className="text-red-500">{errors.status.message}</p>}
                    </FormChild>
                </FormGroup>

                { method === "PUT" &&
                    <>
                        <Input label="Folder Location" name="folder_location" type="text" register={register} tooltip="CAUTION: You should only change this if you change the actual folder location on the server." />
                    {errors.folder_location && <p className="text-red-500">{errors.folder_location.message}</p>}
                    </>
                }

                <FormGroup>
                    <Button className="" onClick={() => navigate("/bids")}>Cancel</Button>
                    <Submit isSubmitting={isProcessing} label="Submit" />
                </FormGroup>
            </GenericFormBGContainer>
        </GenericForm>
    )
}